import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { CSVLink } from "react-csv";
import "react-datepicker/dist/react-datepicker.css";
import NoDataFound from "../../shared/NoDataFound";
import TableLoader from "../../shared/TableLoader";
import arrow from "../../assets/images/arrow_back_icon.svg";
import closeSvg from "../../assets/images/uncheck.svg";
import API from "../../services/api";
import { capitalizeFirstLetter, formatAmount } from "../../utils/constants";
import uparrow from "../../assets/images/chevron-down.svg";
import downarrow from "../../assets/images/chevron-up.svg";
import { SlIcon } from "@shoelace-style/shoelace/dist/react";

function SalesCoverage() {
  const currentDate = new Date();
  currentDate.setDate(1);
  currentDate.setHours(0, 0, 0, 0);

  const [data, setData] = useState([]);
  const [path, setPath] = useState([
    { sales_person_id: 0, name: localStorage.getItem("name") || "Start" },
  ]);
  const [loader, setLoader] = useState(false);
  const [ellaborateLoader, setEllaborateLoader] = useState(false);
  const [sales_person_id, setSales_person_id] = useState(0);
  const [ellaborateData, setEllaborateData] = useState(null);
  const [MonthRange, setMonthRange] = useState([currentDate, currentDate]);
  const downRef = useRef();

  const fetchData = async () => {
    try {
      if (!(MonthRange[0] && MonthRange[1])) return;
      setLoader(true);
      let result = await API({
        url: "effort_analysis/coverage",
        method: "POST",
        responseType: "json",
        data: { sales_person_id, MonthRange },
      });
      if (result.code === 200) {
        setData(result.data);
      }
      setLoader(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
    setEllaborateData(null);
  }, [sales_person_id, MonthRange]);

  const changeName = (item) => {
    if (item.subordinates_count) {
      setSales_person_id(item.sales_person_id);
      setPath([...path, { name: item.name, sales_person_id: item.sales_person_id }]);
    }
    setEllaborateData(null);
  };

  const handleEllaborate = async (item) => {
    try {
      if (!(MonthRange[0] && MonthRange[1])) return;
      if (ellaborateData?.sales_person_id === item.sales_person_id) {
        setEllaborateData(null);
      } else {
        setEllaborateData(item);
        setEllaborateLoader(true);
        let result = await API({
          url: "effort_analysis/ellaborate_data",
          method: "POST",
          responseType: "json",
          data: {
            sales_person_id: item.sales_person_id,
            MonthRange,
            head: item.head,
          },
        });
        if (result.code === 200) {
          setEllaborateData({ ...item, data: result.data });
        }
        setEllaborateLoader(false);
      }
    } catch (error) {}
  };

  const DownloadHandle = () => {
    try {
      downRef.current?.link?.click();
    } catch (error) {
      console.error("Error @ DownloadHandle", error);
    }
  };

  // Prepare CSV data: all rows + one summary row with network_stats only at the end
  const csvDataWithSummary = [
    ...data.map((item) => ({
      name: `${item.name} ${
        item.subordinates_count ? `(${item.subordinates_count})` : ""
      }${item.head ? "(head)" : ""}`,
      brands_count: item.brands_count,
      hard_int_count: item.hard_int_count,
      known: item.known,
      unknown: item.unknown,
      missed: item.missed,
      coverage: (item.coverage || 0).toFixed(2),
      frequency: (item.frequency || 0).toFixed(2),
      spent: `${(+item.spent || 0).toFixed(2)} ${item.usd ? `($${item.usd})` : ""}`,
    }))
  ];

  const csvHeaders = [
    { label: "Sales Person", key: "name" },
    { label: "Brands Count", key: "brands_count" },
    { label: "Hard Int Count", key: "hard_int_count" },
    { label: "Known", key: "known" },
    { label: "UnKnown", key: "unknown" },
    { label: "Missed", key: "missed" },
    { label: "Coverage", key: "coverage" },
    { label: "Frequency", key: "frequency" },
    { label: "Revenue", key: "spent" },
  ];

  return (
    <div className="coverage">
      <CSVLink
        data={csvDataWithSummary}
        headers={csvHeaders}
        filename={"Sales_Coverage.csv"}
        ref={downRef}
      />
      <div className="filters">
        <div
          className="d-flex items-center justify-center bg-slate-200 rounded w-8 h-8"
          onClick={() => {
            DownloadHandle();
          }}
        >
          <sl-tooltip content="Download">
            <SlIcon
              library="lucide"
              name="download"
              className="hover:cursor-pointer hover:text-sky-400 transition-all ease-in-out"
            />
          </sl-tooltip>
        </div>
        <div className="month-select">
          <DatePicker
            selected={MonthRange[0]}
            onChange={(update) => {
              setMonthRange(update);
              setEllaborateData(null);
            }}
            startDate={MonthRange[0]}
            endDate={MonthRange[1]}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            selectsRange
            className="dateChooser"
            maxDate={new Date()}
          />
        </div>
      </div>
      <div className="path">
        {path.map((item, index) => (
          <span
            key={index}
            className={index === path.length - 1 ? "highlight" : ""}
            onClick={() => {
              setSales_person_id(item.sales_person_id);
              setPath([...path.slice(0, index + 1)]);
            }}
          >
            {capitalizeFirstLetter(item.name)}{" "}
            {index !== path.length - 1 ? <img className="arrow" src={arrow} alt="no-img" /> : null}
          </span>
        ))}
      </div>
      <div className="view-body">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Sales Person</th>
              <th>Brands Count</th>
              <th>Hard Int Count</th>
              <th>Known</th>
              <th>UnKnown</th>
              <th>Missed</th>
              <th>Coverage</th>
              <th>Frequency</th>
              <th>Revenue</th>
            </tr>
          </thead>
          {data.length && !loader ? (
            <tbody>
              {data.map((item, index) => (
                <tr
                  key={index}
                  className={
                    item.head
                      ? "head"
                      : item.subordinates_count
                      ? "sub-head"
                      : "end"
                  }
                >
                  <td
                    onClick={() => {
                      changeName(item);
                    }}
                    className="sales-name"
                  >
                    <div>
                      {capitalizeFirstLetter(item.name)}
                      {item.subordinates_count ? <span>{`(${item.subordinates_count})`}</span> : null}
                    </div>
                  </td>
                  <td className="last">
                    <span>{item.brands_count}</span>
                    <img
                      src={ellaborateData?.sales_person_id === item.sales_person_id ? uparrow : downarrow}
                      alt=""
                      onClick={() => {
                        handleEllaborate(item);
                      }}
                    />
                  </td>
                  <td className="green">{item.hard_int_count}</td>
                  <td className="green">
                    <div className="percent">
                      {item.known || "-"}
                      {item.known ? (
                        <span>{((item.known / (item.known + item.unknown)) * 100).toFixed(2)} %</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="red">
                    <div className="percent">
                      {item.unknown || "-"}
                      {item.unknown ? (
                        <span>{((item.unknown / (item.known + item.unknown)) * 100).toFixed(2)} %</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="red">{item.missed || "-"}</td>
                  <td className="green" title={`${item.coverage}%`}>
                    {(item.coverage || 0).toFixed(2)}%
                  </td>
                  <td className="green" title={item.frequency}>
                    {(item.frequency || 0).toFixed(2)}
                  </td>
                  <td className="green d-flex gap-2">
                    <span  title={item.spent}>₹{formatAmount((item.spent || 0).toFixed(2))}</span>
                    {item.usd ? <span  title={item.usd}>${formatAmount((item.usd || 0).toFixed(2))}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          ) : !data.length && !loader ? (
            <NoDataFound colspan={999} />
          ) : (
            <TableLoader colspan={999} />
          )}
        </table>
      </div>

      {/* Ellaborate popup code unchanged */}
      {ellaborateData?.sales_person_id ? (
        <div
          className="popup-wrapper"
          onClick={() => {
            setEllaborateData(null);
          }}
        >
          <div
            className="popup-content"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="ellaborate-table">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <td>Index</td>
                    <th>Brand</th>
                    <th>Sales Person</th>
                    <th>Hard Ints Count</th>
                    <th>Known</th>
                    <th>UnKnown</th>
                    <th>Missed</th>
                    <th>Way2News Spent</th>
                  </tr>
                </thead>
                {ellaborateData?.data?.length && !ellaborateLoader ? (
                  <tbody>
                    {ellaborateData?.data?.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{capitalizeFirstLetter(item.brand_name)}</td>
                        <td>{capitalizeFirstLetter(item.sales_name)}</td>
                        <td className="green">{item.hard_int_count}</td>
                        <td className="green">
                          <div className="percent">
                            {item.known || "-"}
                            {item.known ? (
                              <span>{((item.known / (item.known + item.unknown)) * 100).toFixed(2)} %</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="red">
                          <div className="percent">
                            {item.unknown || "-"}
                            {item.unknown ? (
                              <span>{((item.unknown / (item.known + item.unknown)) * 100).toFixed(2)} %</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="red">{item.missed || "-"}</td>
                        <td className="green" title={item.spent}>
                          {formatAmount((item.spent || 0).toFixed(2))}
                        </td>
                      </tr>
                    ))}
                    <tr className="footer">
                      <td className="sales-name" colSpan={3}>
                        <div className="d-flex gap-3">
                          <div>
                            {capitalizeFirstLetter(ellaborateData.name)}
                            {ellaborateData.subordinates_count ? (
                              <span>{`(${ellaborateData.subordinates_count})`}</span>
                            ) : null}
                          </div>
                          <div>
                            Brands count: <span>{ellaborateData.brands_count}</span>
                          </div>
                          <div>
                            Coverage: <span>{(ellaborateData.coverage || 0).toFixed(2)}%</span>
                          </div>
                          <div>
                            Frequency: <span>{(ellaborateData.frequency || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="green">{ellaborateData.hard_int_count}</td>
                      <td className="green">{ellaborateData.known || "-"}</td>
                      <td className="red">{ellaborateData.unknown || "-"}</td>
                      <td className="red">{ellaborateData.missed || "-"}</td>
                      <td className="green" title={ellaborateData.spent}>
                        <span>{formatAmount((ellaborateData.spent || 0).toFixed(2))}</span>
                      </td>
                    </tr>
                  </tbody>
                ) : !ellaborateData?.data?.length && !ellaborateLoader ? (
                  <NoDataFound colspan={999} />
                ) : (
                  <TableLoader colspan={999} />
                )}
              </table>
            </div>
            <button className="close-btn" onClick={() => setEllaborateData(null)}>
              <img src={closeSvg} alt="close2" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default SalesCoverage;
